import * as React from "react";
import { useCallback, useContext, useEffect, useState } from "react";
import { styled } from "@mui/material";
import { HomePageVisualStepBox } from "./homePageVisualStepBox";
import { HomePageSpecialityIcon } from "./homePageSpecialityIcon";
import {
  ACTION,
  CATEGORY,
  evt,
  isCordovaApp,
  isInStandaloneMode,
  setUserSpeciality,
} from "fe-common/analytics/analytics";
import { Specialities } from "@kahunmedical/user-management";
import { ReactComponent as PhysicianIcon } from "../../../../assets/physician.svg";
import { ReactComponent as StudentIcon } from "../../../../assets/student.svg";
import { ReactComponent as OtherIcon } from "../../../../assets/male_nurse.svg";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { KahunColorMap } from "fe-common/ui/assets/colorMap";
import {
  MobileView,
  SingleViewPerLayoutBreakPoint,
} from "../../../../util/aKaunGeneralUtil";
import { commonBoxShadowStyle } from "fe-common/ui/boxShadow";
import { KahunPrimaryTypography } from "../../../../style/akTypography";
import { writeToGlobalStorage } from "fe-web-components/userStorage/globalUserStorage";
import { UserStorageKey } from "fe-web-components/userStorage/userStorageKey";
import { AgentRoute } from "../../util/agentRoutes";
import { useQueryParams } from "fe-web-components/useQueryParams";
import { isValidSession, UserAttrs } from "@kahunmedical/user-management";
import { useDiagnosticNavigation } from "../../useDiagnosticNavigation";
import { useRouteHelper } from "../../useRouteHelper";
import { ClinicModeContext } from "../../../../context/diagnosticAgent";

//region [[ Styles ]]

const WhoAreYouContentView = styled("div")(({ theme }) => ({
  display: "flex",
  flexFlow: "column",
  alignItems: "center",
  width: "100%",
  [theme.breakpoints.down(MobileView)]: {
    flexFlow: "column-reverse",
  },
}));

const MobileSeperator = styled(ChevronRightIcon)(({ theme }) => ({
  fill: KahunColorMap.Accent,
  display: "none",
  [theme.breakpoints.down(MobileView)]: {
    display: "block",
    height: "1.3em",
    width: "1.3em",
  },
}));

const Title = styled(KahunPrimaryTypography)(({ theme }) => ({
  color: KahunColorMap.Secondary,
  fontSize: "18px",
  lineHeight: "16px",
  fontWeight: 700,
  textTransform: "uppercase",
  [theme.breakpoints.down(MobileView)]: {
    align: "center",
    fontSize: "15px",
    lineHeight: "15px",
  },
}));

const SubTitle = styled(Title)(({ theme }) => ({
  fontSize: "19px",
  lineHeight: "26px",
  fontWeight: 500,
  padding: "20px",
  paddingBottom: 0,
  marginTop: "6px",
  color: KahunColorMap.DefaultLight,
  textAlign: "center",
  textTransform: "none",
  [theme.breakpoints.down(SingleViewPerLayoutBreakPoint)]: {
    fontSize: "15px",
    padding: "2px",
    marginTop: "0",
    lineHeight: "20px",
  },
  "& a, & a:active, & a:hover, & a:focus, & a:visited": {
    textDecoration: "underline",
    color: KahunColorMap.DefaultLight,
  },
}));

const BackText = styled(SubTitle)(({ theme }) => ({
  display: "none",
  fontSize: "14px",
  lineHeight: "20px",
  [theme.breakpoints.down(MobileView)]: { display: "none" },
}));

const StepsSection = styled("div")(({ theme }) => ({
  display: "flex",
  flexFlow: "row",
  justifyContent: "space-evenly",
  position: "relative",
  alignItems: "flex-start",
  width: "50%",
  [theme.breakpoints.down(SingleViewPerLayoutBreakPoint)]: {
    display: "none",
    width: "100%",
    ...commonBoxShadowStyle,
    padding: "17px",
    margin: "5px 0",
    background: KahunColorMap.White,
    borderRadius: "14px",
    justifyContent: "space-evenly",
    flexWrap: "wrap",
  },
  [theme.breakpoints.down(MobileView)]: {
    display: "flex",
  },
}));

const HowItWorksText = styled(BackText)(({ theme }) => ({
  display: "none",
  [theme.breakpoints.down(MobileView)]: {
    display: "flex",
    width: "100%",
    justifyContent: "center",
    alignItems: "center",
    alignText: "center",
    marginBottom: "15px",
  },
}));

const WhoYouAreForm = styled("div")({
  display: "flex",
  flexFlow: "column",
});

const StepContainer = styled("div")(({ theme }) => ({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  [theme.breakpoints.up(SingleViewPerLayoutBreakPoint)]: {
    marginRight: "74px",
    "&:last-of-type": {
      marginRight: 0,
    },
  },
}));

const ConnectingLine = styled("div")(({ theme }) => ({
  position: "absolute",
  top: "50%",
  left: "10%",
  height: "4px",
  width: "80%",
  background: "#C68E11",
  transition: "width 0.5s ease-out",
  [theme.breakpoints.down(MobileView)]: {
    width: "0",
  },
}));

const FormSection = styled("div")(({ theme }) => ({
  display: "flex",
  flexFlow: "row",
  justifyContent: "center",
  margin: "13px",
  [theme.breakpoints.down(MobileView)]: {
    flexFlow: "column",
    margin: 0,
    width: "100%",
  },
}));
//endregion [[ Styles ]]

//region [[ Props ]]

export interface WhoAreYouContentProps {}

//endregion [[ Props ]]

//region [[ Functions ]]
//endregion [[ Functions ]]

//region [[ Texts ]]
const visualStepsTexts = [
  {
    title: "Step 1",
    shortText: "Describe your patient",
    content:
      "Start with age, gender, and chief complaint. " +
      "Use the preconfigured panels and autocomplete search to add " +
      "findings, background, and workup results.",
  },
  {
    title: "Step 2",
    shortText: "Get Suggestions",
    content:
      "Kahun’s AI algorithm suggests an initial differential diagnosis" +
      " and suggests further workup to narrow down the list",
  },
  {
    title: "Step 3",
    shortText: "See the Reasoning",
    content:
      "Expand the results to see how they relate to the specified findings, " +
      "or drill down to see the reasoning and the original knowledge sources.",
  },
];

const supportedSpecialities = [
  {
    displayText: "I'm a Physician",
    speciality: Specialities.Physician,
    icon: PhysicianIcon,
  },
  {
    displayText: "I'm a Med Student",
    speciality: Specialities.Student,
    icon: StudentIcon,
  },
  {
    displayText: "Other",
    speciality: Specialities.Other,
    icon: OtherIcon,
  },
];

const INTRO_TEXT = "Before we start, please tell us about yourself:";
const HOW_IT_WORKS = "How it works:";
//endregion [[ Texts ]]

export const WhoAreYouContent = ({ ...props }: WhoAreYouContentProps) => {
  //When affiliated from partner, force login
  const queryParams = useQueryParams();

  const partnerId = queryParams.get("pid");

  const { loginPopupState } = useDiagnosticNavigation();

  const [isUserLoggedIn, setIsUserLoggedIn] = useState(false);

  const { navigateTo, getNextRoute, goToLoginPage } = useRouteHelper();

  const clinicMode = useContext(ClinicModeContext);

  // See if user got logged in, and also wait to see if s/he logged in from popup
  useEffect(() => {
    (async () => {
      const isValid = await isValidSession();
      setIsUserLoggedIn(isValid);
    })();
  }, [loginPopupState]);

  // Check if we already have the user information written in local storage
  useEffect(() => {
    if (
      (localStorage.getItem(UserAttrs.SPECIALITY) &&
        localStorage.getItem(UserAttrs.SPECIALITY) !== "null") ||
      isUserLoggedIn ||
      clinicMode
    ) {
      // Save also in analytics
      if (!isUserLoggedIn && !clinicMode) {
        setUserSpeciality(localStorage.getItem(UserAttrs.SPECIALITY));
      }
      // if so redirect to next route
      navigateTo({ route: getNextRoute()?.route as AgentRoute });
    }
  }, [isUserLoggedIn, navigateTo, getNextRoute, clinicMode]);

  const createSpecialityHandler = useCallback(
    (specialityObject) => () => {
      // Send to mix panel user selected the speciality
      evt({
        category: CATEGORY.WELCOME_SCREEN,
        action: ACTION.SPECIALTY_SELECTED,
        value: specialityObject.speciality,
        fb:
          specialityObject.speciality === Specialities.Student ||
          specialityObject.speciality === Specialities.Physician,
        addValueToFB: true,
      });
      // Save the data in local storage
      writeToGlobalStorage(
        UserStorageKey.Speciality,
        specialityObject.speciality,
      );
      // Save also in analytics
      setUserSpeciality(specialityObject.speciality);
      // GO to next step
      if (partnerId) {
        goToLoginPage();
      } else {
        navigateTo({ route: getNextRoute()?.route as AgentRoute });
      }
    },
    [partnerId, navigateTo, getNextRoute, goToLoginPage],
  );

  return (
    <WhoAreYouContentView>
      <StepsSection>
        <HowItWorksText>{HOW_IT_WORKS}</HowItWorksText>
        <ConnectingLine />
        {visualStepsTexts.map((item, idx) => (
          <StepContainer key={idx}>
            <HomePageVisualStepBox key={idx} visualStepItem={item} />
            {idx < visualStepsTexts.length - 1 && <MobileSeperator />}
          </StepContainer>
        ))}
      </StepsSection>
      <WhoYouAreForm>
        <SubTitle>{INTRO_TEXT}</SubTitle>
        <FormSection>
          {supportedSpecialities.map((specialityObject) => (
            <HomePageSpecialityIcon
              key={specialityObject.speciality}
              displayText={specialityObject.displayText}
              onClick={createSpecialityHandler(specialityObject)}
              icon={specialityObject.icon}
            />
          ))}
        </FormSection>
        {!isCordovaApp() && !isInStandaloneMode() && (
          <BackText>
            Back to <a href={"https://kahun.com"}>Kahun Website</a>
          </BackText>
        )}
      </WhoYouAreForm>
    </WhoAreYouContentView>
  );
};
